import React, { useState } from 'react';
import {
  AppBar, Toolbar, Box, IconButton, InputBase, Menu, MenuItem,
  List, ListItem, ListItemText, Paper, Popper, ClickAwayListener, Typography, Divider,
  useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { useAuth } from '../../contexts/AuthContext';
import { useModules } from '../../contexts/ModuleContext';
import { searchAPI } from '../../services/api';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';

interface TopbarProps {
  title?: string;
}

export const Topbar: React.FC<TopbarProps> = ({ title }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const { collapsed, setMobileOpen } = useModules();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searchAnchor, setSearchAnchor] = useState<null | HTMLElement>(null);

  const sidebarWidth = isMobile ? 0 : (collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH);

  const handleSearch = async (q: string) => {
    setQuery(q);
    if (isSuperAdmin || q.length < 2) { setResults([]); setSearchOpen(false); return; }
    try {
      const res = await searchAPI.search(q);
      setResults(res.data);
      setSearchOpen(true);
    } catch { setResults([]); }
  };

  const handleResultClick = (r: any) => {
    setSearchOpen(false);
    setQuery('');
    if (r.path) navigate(r.path);
    else if (r.type === 'custom') navigate(`/crm/${r.moduleKey}/${r.recordId}`);
    else navigate(r.path || `/${r.moduleKey}`);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: '#fff',
        color: brandColors.textMuted,
        borderBottom: `1px solid ${brandColors.border}`,
        boxShadow: brandColors.shadowSm,
        width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
        ml: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'width 0.2s, margin 0.2s',
      }}
    >
      <Toolbar sx={{ gap: { xs: 1, md: 2 }, minHeight: { xs: 56, md: 64 }, flexWrap: 'nowrap', px: { xs: 1.5, md: 2 } }}>
        {isMobile && (
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ color: brandColors.textMuted, mr: 0.5 }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        )}
        {title && (
          <Typography variant="h6" sx={{ color: brandColors.textDark, fontWeight: 700, mr: 1, display: { xs: 'none', lg: 'block' } }}>
            {title}
          </Typography>
        )}

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', maxWidth: { xs: '100%', md: 520 }, mx: 'auto', minWidth: 0 }}>
          <Paper
            component="form"
            sx={{
              display: 'flex', alignItems: 'center', px: 2, py: 0.6, width: '100%',
              bgcolor: brandColors.contentBg, boxShadow: 'none',
              border: `1px solid ${brandColors.border}`, borderRadius: 2.5,
              '&:focus-within': {
                borderColor: brandCss.primary,
                boxShadow: brandCss.focusRing,
              },
            }}
            onSubmit={(e) => e.preventDefault()}
          >
            <SearchIcon sx={{ color: brandColors.textMuted, mr: 1, fontSize: 20 }} />
            <InputBase
              placeholder={isSuperAdmin ? 'Search tenants, plans...' : 'Search modules, records, trips...'}
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={(e) => { if (!isSuperAdmin) { setSearchAnchor(e.currentTarget.parentElement); setSearchOpen(results.length > 0); } }}
              sx={{ flex: 1, fontSize: 13 }}
              disabled={isSuperAdmin}
            />
          </Paper>
        </Box>

        <Popper open={searchOpen && results.length > 0} anchorEl={searchAnchor} placement="bottom" sx={{ zIndex: 1400, width: { xs: 'min(100vw - 32px, 480px)', sm: 480 } }}>
          <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
            <Paper elevation={8} sx={{ mt: 1, maxHeight: 320, overflow: 'auto', borderRadius: 2 }}>
              <List dense>
                {results.map((r, i) => (
                  <ListItem key={i} button onClick={() => handleResultClick(r)} sx={{ py: 1.5, '&:hover': { bgcolor: brandCss.green50 } }}>
                    <ListItemText primary={r.title} secondary={`${r.moduleLabel} · ${r.type}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>

        <NotificationCenter />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={(e) => setAnchor(e.currentTarget)}>
          <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: brandColors.textDark, lineHeight: 1.2 }}>
              {!isSuperAdmin && user?.company?.companyName
                ? user.company.companyName
                : (user?.name || 'Account')}
            </Typography>
            <Typography variant="caption" sx={{ color: brandColors.textMuted, display: 'block', lineHeight: 1.2 }}>
              {!isSuperAdmin && user?.company?.companyName
                ? (user?.name || '')
                : (isSuperAdmin ? 'Super Admin' : '')}
            </Typography>
          </Box>
        </Box>

        <Menu
          anchorEl={anchor}
          open={!!anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: `${RADIUS}px`, minWidth: 220, mt: 1 } }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: brandColors.textDark, lineHeight: 1.3 }}>
              {isSuperAdmin ? 'Super Admin' : (user?.company?.companyName || 'Company')}
            </Typography>
            <Typography variant="caption" sx={{ color: brandColors.textMuted, display: 'block', mt: 0.25 }}>
              {user?.name}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchor(null); navigate('/profile'); }}>Profile</MenuItem>
          <MenuItem onClick={() => { setAnchor(null); navigate(isSuperAdmin ? '/super-admin/settings' : '/settings'); }}>Settings</MenuItem>
          {!isSuperAdmin && (
            <MenuItem onClick={() => { setAnchor(null); navigate('/roles'); }}>Staff & Login</MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => { logout(); navigate('/login'); }} sx={{ color: brandColors.danger }}>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
